import type { Asset } from "@studio/shared";
import { AudioLines, FileText, Film, Image as ImageIcon } from "lucide-react";
import { cn, formatDuration } from "@/lib/format";

/**
 * A result or reference as it sits on the contact sheet: the picture fills the frame, and the edge code
 * underneath says what it is (kind, length, size) the way film edge markings do.
 */
export function MediaThumb({
  asset,
  className,
  fit = "cover",
  edge = true,
  develop = false,
}: {
  asset: Asset;
  className?: string;
  fit?: "cover" | "contain";
  edge?: boolean;
  develop?: boolean;
}) {
  const Icon = asset.kind === "video" ? Film : asset.kind === "audio" ? AudioLines : asset.kind === "document" ? FileText : ImageIcon;
  return (
    <div className={cn("relative overflow-hidden rounded-[var(--radius-media)] bg-panel", className)}>
      {asset.thumbUrl ? (
        <img
          src={asset.thumbUrl}
          alt={asset.prompt ?? asset.name ?? ""}
          loading="lazy"
          decoding="async"
          draggable={false}
          className={cn(
            "size-full",
            fit === "cover" ? "object-cover" : "object-contain",
            // a page reads from its top: keep the heading in the frame
            asset.kind === "document" && "bg-white object-top",
            develop && "animate-develop",
          )}
        />
      ) : asset.kind === "document" ? (
        <div className="flex size-full items-center justify-center bg-raised">
          <FileText className="size-1/3 text-mute" aria-hidden />
        </div>
      ) : (
        <AudioFace asset={asset} />
      )}
      {edge ? (
        <div data-edge className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-6 text-[11px] font-medium text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.6)]">
          <Icon className="size-3.5 opacity-80" aria-hidden />
          <span className="tabular">
            {asset.kind === "image"
              ? `${asset.width ?? ""}×${asset.height ?? ""}`
              : asset.kind === "document"
                ? documentLength(asset)
                : formatDuration(asset.durationSec)}
          </span>
          {asset.kind === "video" && asset.hasAudio ? <AudioLines className="ml-auto size-3.5 opacity-70" aria-label="with sound" /> : null}
        </div>
      ) : null}
    </div>
  );
}

export function documentLength(asset: Asset): string {
  return asset.pages ? `${asset.pages} ${asset.pages === 1 ? "page" : "pages"}` : "PDF";
}

/** Audio has no picture: draw a quiet waveform band so a song still reads as a thing you can pick. */
function AudioFace({ asset }: { asset: Asset }) {
  const bars = Array.from({ length: 18 }, (_, i) => {
    const seed = (asset.id.charCodeAt(i % asset.id.length) * (i + 3)) % 97;
    return 18 + (seed % 60);
  });
  return (
    <div className="flex size-full items-center justify-center gap-[3px] bg-raised px-[12%]">
      {bars.map((height, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: static decoration
          key={i}
          className="min-w-[2px] max-w-[4px] flex-1 rounded-full bg-signal/70"
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
}
